import { Flex } from "@lazarentertainment/web-design-system/components/flex"
import { Truncate } from "@lazarentertainment/web-design-system/components/truncate"
import { Countdown } from "client/components/channel/Countdown"
import { Badge } from "client/designSystem/Badge"
import Image from "next/image"
import { ReactNode, useMemo } from "react"
import { useIntl } from "react-intl"

import styles from "./FeaturedUpcomingShowCard.module.css"

export const DEFAULT_SPLASH_IMAGE_URL = "/static/default-splash.png"

export type FeaturedUpcomingShowCardProps = {
  /**
   * The date (in ms) to count down towards.
   */
  countdownDateMs: number

  /**
   * The title of the upcoming show.
   */
  title: string

  /**
   * The title of the channel/store, listed above the show's title.
   */
  channelTitle: string

  /**
   * The description of the upcoming show.
   */
  description?: string

  /**
   * ReactNode for the splash image. A ReactNode is accepted
   * for the intention of using Next.js Image components.
   */
  splashImage?: ReactNode

  /**
   * ReactNode for the avatar image. A ReactNode is accepted
   * for the intention of using Next.js Image components.
   */
  avatarImage?: ReactNode

  /**
   * When true, the splashImage will use `object-fit: contain` when in mobile mode (<800px width).
   * This is needed for the /embed page, where we need the card to fit within ANY size of embed,
   * and we don't have the luxury of allowing the page to scroll vertically.
   *
   * Without compact mode, the card would be too tall for mobile mode in lots of cases.
   *
   * @default false
   */
  compactMode?: boolean
}

export const FeaturedUpcomingShowCard = ({
  countdownDateMs,
  title,
  description,
  channelTitle,
  splashImage,
  avatarImage,
  compactMode = false,
}: FeaturedUpcomingShowCardProps) => {
  const intl = useIntl()
  const countdownDateText = useMemo(() => {
    return new Date(countdownDateMs).toLocaleString(intl.locale, {
      weekday: "long",
      month: "long",
      day: "numeric",
    })
  }, [countdownDateMs])
  const countdownTimeText = useMemo(() => {
    return new Date(countdownDateMs).toLocaleString(intl.locale, {
      hour: "numeric",
      minute: "2-digit",
    })
  }, [countdownDateMs])

  return (
    <div
      className={styles.featuredUpcomingShowCard}
      data-compact-mode={compactMode}
    >
      <div className={styles.splashImageContainer}>
        {splashImage ?? (
          <Image
            src="/static/showDefaultThumbnail.png"
            width={834}
            height={500}
            alt={title}
            draggable={false}
          />
        )}
        <Badge variant="overlay" className={styles.upcomingShowBadge}>
          Upcoming show
        </Badge>
      </div>
      <div className={styles.sidePanel}>
        <Flex gap="var(--gap-sm)" align="stretch">
          <Flex direction="row" gap="var(--gap-2xs)">
            {avatarImage}
            <span className={`body-md text-color-tertiary`}>
              {channelTitle}
            </span>
          </Flex>
          <Flex align="stretch" gap="var(--gap-3xs)">
            <h2 className={`title-lg text-color-primary`}>{title}</h2>
            {!!description && (
              <Truncate
                lineCount={4}
                containerElement="p"
                className={`body-md text-color-secondary-subdued`}
              >
                {description}
              </Truncate>
            )}
          </Flex>
        </Flex>
        <Flex>
          <Countdown
            size="lg"
            variant="rose"
            countdownDateMs={countdownDateMs}
          />

          <Flex direction="row" gap="var(--gap-3xs)">
            <span className={`title-xs text-color-secondary`}>
              {countdownDateText}
            </span>
            <span className={`body-md text-color-tertiary`}>
              {countdownTimeText}
            </span>
          </Flex>
        </Flex>
      </div>
    </div>
  )
}
